(() => {
  const GLOBAL_KEY = '__xyxxOccasionsCarousel';
  const ROOT_SELECTOR = '[data-xyxx-occasions]';

  if (window[GLOBAL_KEY]?.loaded) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  const instances = new WeakMap();

  class XyxxOccasionsCarousel {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-occasion-stage]');
      this.track = root.querySelector('[data-occasion-track]');
      this.realSlides = Array.from(
        root.querySelectorAll('[data-occasion-slide]')
      );
      this.dots = Array.from(root.querySelectorAll('[data-occasion-dot]'));
      this.previousButton = root.querySelector('[data-occasion-previous]');
      this.nextButton = root.querySelector('[data-occasion-next]');

      this.realCount = this.realSlides.length;
      this.realIndex = Number.parseInt(
        root.dataset.initialIndex || '0',
        10
      );
      this.displayIndex = 0;
      this.currentTranslate = 0;
      this.pointerId = null;
      this.dragStartX = 0;
      this.dragStartTranslate = 0;
      this.dragDistance = 0;
      this.isAnimating = false;
      this.queuedDirection = 0;
      this.queuedDot = null;
      this.transitionTimer = null;
      this.resizeFrame = null;
      this.instantFrame = null;
      this.dotHandlers = new Map();
      this.breakpointQuery = window.matchMedia('(max-width: 989px)');

      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onLostPointerCapture = this.onLostPointerCapture.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onTrackTransitionEnd =
        this.onTrackTransitionEnd.bind(this);

      if (!this.stage || !this.track || this.realCount < 2) return;

      this.realIndex = this.normalizeIndex(this.realIndex);
      this.createLoopSlides();
      this.displayIndex = this.realCount + this.realIndex;
      this.bindEvents();
      this.bindResponsiveObservers();

      requestAnimationFrame(() => this.refreshLayout());
    }

    createLoopSlides() {
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      this.beforeClones = this.realSlides.map((slide, index) => {
        const clone = slide.cloneNode(true);
        this.prepareClone(clone, index);
        beforeFragment.appendChild(clone);
        return clone;
      });

      this.afterClones = this.realSlides.map((slide, index) => {
        const clone = slide.cloneNode(true);
        this.prepareClone(clone, index);
        afterFragment.appendChild(clone);
        return clone;
      });

      this.track.insertBefore(beforeFragment, this.realSlides[0]);
      this.track.appendChild(afterFragment);

      this.slides = Array.from(
        this.track.querySelectorAll('[data-occasion-slide]')
      );
    }

    prepareClone(clone, realIndex) {
      clone.classList.remove('is-active');
      clone.classList.add('is-clone');
      clone.dataset.realIndex = String(realIndex);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');

      clone.querySelectorAll('[id]').forEach((element) => {
        element.removeAttribute('id');
      });

      clone.querySelectorAll('a, button, input, select, textarea').forEach(
        (element) => {
          element.tabIndex = -1;
        }
      );
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.stage.addEventListener('keydown', this.onKeydown);
      this.stage.addEventListener('pointerdown', this.onPointerDown);
      this.stage.addEventListener('pointermove', this.onPointerMove);
      this.stage.addEventListener('pointerup', this.onPointerUp);
      this.stage.addEventListener('pointercancel', this.onPointerUp);
      this.stage.addEventListener(
        'lostpointercapture',
        this.onLostPointerCapture
      );
      this.track.addEventListener(
        'transitionend',
        this.onTrackTransitionEnd
      );

      this.dots.forEach((dot) => {
        const handler = () => {
          this.goToDot(Number.parseInt(dot.dataset.index || '0', 10));
        };

        this.dotHandlers.set(dot, handler);
        dot.addEventListener('click', handler);
      });
    }

    bindResponsiveObservers() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.root);
        this.resizeObserver.observe(this.stage);
      }

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, {
        passive: true,
      });
      window.visualViewport?.addEventListener('resize', this.onResize, {
        passive: true,
      });

      if (this.breakpointQuery.addEventListener) {
        this.breakpointQuery.addEventListener('change', this.onResize);
      } else {
        this.breakpointQuery.addListener(this.onResize);
      }

      document.fonts?.ready.then(() => {
        if (this.root.isConnected) this.scheduleRefresh();
      });
    }

    normalizeIndex(index) {
      return ((index % this.realCount) + this.realCount) % this.realCount;
    }

    onPrevious() {
      this.goBy(-1);
    }

    onNext() {
      this.goBy(1);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.onPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.onNext();
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToDot(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goToDot(this.realCount - 1);
      }
    }

    onPointerDown(event) {
      if (this.isAnimating) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartTranslate = this.currentTranslate;
      this.dragDistance = 0;

      this.stage.classList.add('is-dragging');
      this.stage.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;

      this.dragDistance = event.clientX - this.dragStartX;
      this.setTranslate(this.dragStartTranslate + this.dragDistance);
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;

      const threshold = Math.min(70, this.stage.clientWidth * 0.12);
      const direction = this.dragDistance < 0 ? 1 : -1;
      const shouldMove = Math.abs(this.dragDistance) >= threshold;

      this.endPointerInteraction(event.pointerId);

      if (shouldMove) {
        this.goBy(direction);
      } else {
        this.startAnimatedRender();
      }
    }

    onLostPointerCapture(event) {
      if (event.pointerId !== this.pointerId) return;

      this.pointerId = null;
      this.dragDistance = 0;
      this.stage.classList.remove('is-dragging');
      this.startAnimatedRender();
    }

    endPointerInteraction(pointerId = this.pointerId) {
      this.stage.classList.remove('is-dragging');
      this.pointerId = null;
      this.dragDistance = 0;

      if (
        pointerId !== null &&
        this.stage.hasPointerCapture?.(pointerId)
      ) {
        this.stage.releasePointerCapture(pointerId);
      }
    }

    onResize() {
      this.scheduleRefresh();
    }

    scheduleRefresh() {
      cancelAnimationFrame(this.resizeFrame);

      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          this.refreshLayout();
        });
      });
    }

    refreshLayout() {
      if (!this.root.isConnected || this.stage.clientWidth <= 0) return;

      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
      this.isAnimating = false;
      this.queuedDirection = 0;
      this.queuedDot = null;
      this.endPointerInteraction();
      this.displayIndex = this.realCount + this.realIndex;
      this.render(false);
    }

    onTrackTransitionEnd(event) {
      if (
        event.target !== this.track ||
        event.propertyName !== 'transform'
      ) {
        return;
      }

      this.finishTransition();
    }

    goBy(direction) {
      if (this.isAnimating) {
        this.queuedDirection = direction;
        this.queuedDot = null;
        return;
      }

      this.realIndex = this.normalizeIndex(this.realIndex + direction);
      this.displayIndex += direction;
      this.startAnimatedRender();
    }

    goToDot(targetIndex) {
      const normalizedTarget = this.normalizeIndex(targetIndex);

      if (this.isAnimating) {
        this.queuedDot = normalizedTarget;
        this.queuedDirection = 0;
        return;
      }

      let difference = normalizedTarget - this.realIndex;
      const half = this.realCount / 2;

      if (difference > half) {
        difference -= this.realCount;
      } else if (difference < -half) {
        difference += this.realCount;
      }

      if (difference === 0) return;

      this.realIndex = normalizedTarget;
      this.displayIndex += difference;
      this.startAnimatedRender();
    }

    startAnimatedRender() {
      this.isAnimating = true;
      this.render(true);

      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = window.setTimeout(() => {
        this.finishTransition();
      }, 540);
    }

    finishTransition() {
      if (!this.isAnimating) return;

      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;

      if (this.displayIndex < this.realCount) {
        this.displayIndex += this.realCount;
        this.render(false);
      } else if (this.displayIndex >= this.realCount * 2) {
        this.displayIndex -= this.realCount;
        this.render(false);
      }

      this.isAnimating = false;

      const queuedDot = this.queuedDot;
      const queuedDirection = this.queuedDirection;
      this.queuedDot = null;
      this.queuedDirection = 0;

      if (queuedDot !== null) {
        requestAnimationFrame(() => this.goToDot(queuedDot));
      } else if (queuedDirection !== 0) {
        requestAnimationFrame(() => this.goBy(queuedDirection));
      }
    }

    render(animate = true) {
      cancelAnimationFrame(this.instantFrame);
      this.stage.classList.toggle('is-instant', !animate);

      this.updateSlideState();
      this.updateDots();

      const activeSlide = this.slides[this.displayIndex];
      const stageWidth = this.stage.clientWidth;

      if (!activeSlide || stageWidth <= 0) return;

      const slideWidth = activeSlide.getBoundingClientRect().width;
      const stageCenter = stageWidth / 2;
      const slideCenter = activeSlide.offsetLeft + slideWidth / 2;

      this.currentTranslate = stageCenter - slideCenter;
      this.setTranslate(this.currentTranslate);

      if (!animate) {
        this.track.getBoundingClientRect();
        this.instantFrame = requestAnimationFrame(() => {
          this.instantFrame = null;
          this.stage.classList.remove('is-instant');
        });
      }
    }

    updateSlideState() {
      this.slides.forEach((slide, slideIndex) => {
        const isVisualActive = slideIndex === this.displayIndex;
        const isMiddleRealSlide =
          slideIndex >= this.realCount &&
          slideIndex < this.realCount * 2;
        const isAccessibleActive =
          isMiddleRealSlide &&
          slideIndex - this.realCount === this.realIndex;

        slide.classList.toggle('is-active', isVisualActive);
        slide.setAttribute(
          'aria-hidden',
          isAccessibleActive ? 'false' : 'true'
        );
      });
    }

    updateDots() {
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.realIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.tabIndex = isActive ? 0 : -1;
      });
    }

    setTranslate(value) {
      const roundedValue = Math.round(value * 1000) / 1000;
      this.track.style.transform =
        `translate3d(${roundedValue}px, 0, 0)`;
    }

    destroy() {
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.instantFrame);
      window.clearTimeout(this.transitionTimer);
      this.resizeObserver?.disconnect();
      this.endPointerInteraction();

      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      window.visualViewport?.removeEventListener('resize', this.onResize);

      if (this.breakpointQuery.removeEventListener) {
        this.breakpointQuery.removeEventListener('change', this.onResize);
      } else {
        this.breakpointQuery.removeListener(this.onResize);
      }

      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.stage?.removeEventListener('keydown', this.onKeydown);
      this.stage?.removeEventListener('pointerdown', this.onPointerDown);
      this.stage?.removeEventListener('pointermove', this.onPointerMove);
      this.stage?.removeEventListener('pointerup', this.onPointerUp);
      this.stage?.removeEventListener('pointercancel', this.onPointerUp);
      this.stage?.removeEventListener(
        'lostpointercapture',
        this.onLostPointerCapture
      );
      this.track?.removeEventListener(
        'transitionend',
        this.onTrackTransitionEnd
      );

      this.dotHandlers.forEach((handler, dot) => {
        dot.removeEventListener('click', handler);
      });
      this.dotHandlers.clear();

      this.beforeClones?.forEach((clone) => clone.remove());
      this.afterClones?.forEach((clone) => clone.remove());
      this.track?.style.removeProperty('transform');
      this.stage?.classList.remove('is-dragging', 'is-instant');
      this.root.removeAttribute('data-occasions-initialized');
    }
  }

  function getRoots(root = document) {
    const sections = [];

    if (root instanceof Element && root.matches(ROOT_SELECTOR)) {
      sections.push(root);
    }

    root.querySelectorAll?.(ROOT_SELECTOR).forEach((section) => {
      sections.push(section);
    });

    return sections;
  }

  function init(root = document) {
    getRoots(root).forEach((section) => {
      if (
        section.dataset.occasionsInitialized === 'true' ||
        section.dataset.layout !== 'reference_carousel'
      ) {
        return;
      }

      section.dataset.occasionsInitialized = 'true';
      instances.set(section, new XyxxOccasionsCarousel(section));
    });
  }

  function unload(section) {
    const root = section.matches?.(ROOT_SELECTOR)
      ? section
      : section.querySelector?.(ROOT_SELECTOR);

    if (!root) return;

    instances.get(root)?.destroy();
    instances.delete(root);
  }

  window[GLOBAL_KEY] = {
    loaded: true,
    init,
    unload,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), {
      once: true,
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    unload(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);

    if (root) {
      requestAnimationFrame(() => instances.get(root)?.refreshLayout());
    }
  });
})();
